import React, { useState, useRef, useEffect } from 'react';
import {
    Modal,
    View,
    Text,
    TextInput,
    TouchableOpacity,
    FlatList,
    StyleSheet,
    Animated,
    Dimensions,
    Easing,
    Image,
    ActivityIndicator,
} from 'react-native';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { Colors } from '@/constants/theme';
import { fetchChannelUsers } from '@/services/universe/client';
import type { ChannelUser } from '@/services/universe/types';
import { UniverseStreamChannel } from '@/services/stream/types';

const SCREEN_HEIGHT = Dimensions.get('window').height;

const AVATAR_COLORS = ['#4A90D9', '#7B68EE', '#5BA65B', '#E87722', '#C54B8C', '#14a5c6', '#E05C5C'];

function avatarColor(name: string): string {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

interface TransferChatModalProps {
    visible: boolean;
    onClose: () => void;
    onTransfer: (agentId: number) => void | Promise<void>;
    channel?: UniverseStreamChannel;
}

export function TransferChatModal({ visible, onClose, onTransfer, channel }: TransferChatModalProps) {
    const [users, setUsers]             = useState<ChannelUser[]>([]);
    const [loading, setLoading]         = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage]               = useState(1);
    const [lastPage, setLastPage]       = useState(1);
    const [search, setSearch]           = useState('');
    const [selectedId, setSelectedId]   = useState<number | null>(null);
    const [transferring, setTransferring] = useState(false);
    const [imgErrors, setImgErrors]     = useState<Record<number, boolean>>({});

    const slideAnim = useRef(new Animated.Value(SCREEN_HEIGHT)).current;
    const fadeAnim  = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if (visible && channel?.id) {
            setPage(1);
            loadUsers(1);
        }
    }, [visible, channel?.id]);

    const loadUsers = async (pageNum: number) => {
        if (!channel?.id) return;

        if (pageNum === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        try {
            const response = await fetchChannelUsers(channel.id, pageNum);
            if (response?.success && response?.data) {
                if (pageNum === 1) {
                    setUsers(response.data);
                } else {
                    setUsers(prev => [...prev, ...response.data]);
                }
                if (response.meta) {
                    setLastPage(response.meta.last_page);
                    setPage(response.meta.current_page);
                }
            }
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleLoadMore = () => {
        if (!loading && !loadingMore && page < lastPage) {
            loadUsers(page + 1);
        }
    };

    useEffect(() => {
        if (visible) {
            Animated.sequence([
                Animated.timing(fadeAnim, { toValue: 1, duration: 250, useNativeDriver: true }),
                Animated.timing(slideAnim, { toValue: 0, duration: 350, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
            ]).start();
        }
    }, [visible]);

    const handleClose = () => {
        Animated.sequence([
            Animated.timing(slideAnim, { toValue: SCREEN_HEIGHT, duration: 300, easing: Easing.in(Easing.cubic), useNativeDriver: true }),
            Animated.timing(fadeAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
        ]).start(() => {
            setSelectedId(null);
            setImgErrors({});
            setSearch('');
            setUsers([]);
            onClose();
        });
    };

    const handleTransfer = async () => {
        if (!selectedId || transferring) return;
        setTransferring(true);
        try {
            await onTransfer(selectedId);
            handleClose();
        } catch {
            // Parent already surfaced the error; keep the modal open for retry.
        } finally {
            setTransferring(false);
        }
    };

    const filteredUsers = search.trim()
        ? users.filter(u =>
            (u.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (u.position ?? '').toLowerCase().includes(search.toLowerCase())
          )
        : users;

    const renderUser = ({ item }: { item: ChannelUser }) => {
        const hasImage = !!item.image && !imgErrors[item.id];
        return (
            <TouchableOpacity style={styles.row} activeOpacity={0.7} onPress={() => setSelectedId(item.id)}>
                {hasImage ? (
                    <Image
                        source={{ uri: item.image }}
                        style={styles.avatar}
                        onError={() => setImgErrors(prev => ({ ...prev, [item.id]: true }))}
                    />
                ) : (
                    <View style={[styles.avatar, styles.avatarFallback, { backgroundColor: avatarColor(item.name) }]}>
                        <Text style={styles.avatarText}>{item.name.charAt(0)}</Text>
                    </View>
                )}
                <View style={styles.agentInfo}>
                    <Text style={styles.agentName}>{item.name}</Text>
                    <Text style={styles.agentPosition}>{item.position?.trim()}</Text>
                </View>
                <View style={[styles.radio, selectedId === item.id && styles.radioSelected]}>
                    {selectedId === item.id && <View style={styles.radioDot} />}
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <Modal visible={visible} transparent animationType="none" onRequestClose={handleClose}>
            <View style={styles.container}>
                <Animated.View style={[StyleSheet.absoluteFill, styles.backdrop, { opacity: fadeAnim }]}>
                    <TouchableOpacity style={{ flex: 1 }} activeOpacity={1} onPress={handleClose} />
                </Animated.View>

                <Animated.View style={[styles.card, { transform: [{ translateY: slideAnim }] }]}>
                    <View style={styles.header}>
                        <Text style={styles.title}>Transfer Chat</Text>
                        <TouchableOpacity onPress={handleClose} hitSlop={12}>
                            <FontAwesome name="times" size={18} color="#333" />
                        </TouchableOpacity>
                    </View>

                    <View style={styles.searchBar}>
                        <FontAwesome name="search" size={14} color="#999" />
                        <TextInput
                            style={styles.searchInput}
                            placeholder="Search..."
                            placeholderTextColor="#aaa"
                            value={search}
                            onChangeText={setSearch}
                            autoCorrect={false}
                            autoCapitalize="none"
                        />
                    </View>

                    {loading ? (
                        <ActivityIndicator size="large" color={Colors.brand} style={styles.loader} />
                    ) : (
                        <FlatList
                            data={filteredUsers}
                            keyExtractor={item => String(item.id)}
                            style={styles.list}
                            keyboardShouldPersistTaps="handled"
                            renderItem={renderUser}
                            ItemSeparatorComponent={() => <View style={styles.separator} />}
                            onEndReached={handleLoadMore}
                            onEndReachedThreshold={0.5}
                            ListFooterComponent={
                                loadingMore
                                    ? <ActivityIndicator size="small" color={Colors.brand} style={styles.footerLoader} />
                                    : null
                            }
                            ListEmptyComponent={
                                <Text style={styles.emptyText}>No users found</Text>
                            }
                        />
                    )}

                    <View style={styles.footer}>
                        <TouchableOpacity
                            style={[styles.transferBtn, !selectedId && styles.transferBtnDisabled]}
                            activeOpacity={0.8}
                            onPress={handleTransfer}
                            disabled={!selectedId || transferring}
                        >
                            {transferring
                                ? <ActivityIndicator size="small" color="#fff" />
                                : <Text style={styles.transferBtnText}>Transfer</Text>}
                        </TouchableOpacity>
                    </View>
                </Animated.View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    backdrop: {
        backgroundColor: 'rgba(0,0,0,0.45)',
    },
    card: {
        width: '100%',
        maxWidth: 480,
        maxHeight: '82%',
        backgroundColor: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingVertical: 16,
    },
    title: {
        fontSize: 17,
        fontWeight: '700',
        color: '#1A1A1A',
    },
    searchBar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F2F2F2',
        marginHorizontal: 16,
        marginBottom: 8,
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 9,
        gap: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 15,
        color: '#1A1A1A',
        padding: 0,
    },
    loader: {
        marginVertical: 40,
    },
    footerLoader: {
        paddingVertical: 16,
    },
    emptyText: {
        textAlign: 'center',
        color: '#888',
        marginVertical: 32,
        fontSize: 14,
    },
    list: {
        flexGrow: 0,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 12,
    },
    avatar: {
        width: 44,
        height: 44,
        borderRadius: 22,
    },
    avatarFallback: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarText: {
        color: '#fff',
        fontSize: 18,
        fontWeight: '600',
    },
    agentInfo: {
        flex: 1,
        marginLeft: 12,
    },
    agentName: {
        fontSize: 15,
        fontWeight: '600',
        color: '#1A1A1A',
    },
    agentPosition: {
        fontSize: 13,
        color: '#888',
        marginTop: 2,
    },
    radio: {
        width: 22,
        height: 22,
        borderRadius: 11,
        borderWidth: 2,
        borderColor: '#CCC',
        justifyContent: 'center',
        alignItems: 'center',
    },
    radioSelected: {
        borderColor: Colors.brand,
    },
    radioDot: {
        width: 11,
        height: 11,
        borderRadius: 6,
        backgroundColor: Colors.brand,
    },
    separator: {
        height: StyleSheet.hairlineWidth,
        backgroundColor: '#EFEFEF',
        marginLeft: 76,
    },
    footer: {
        padding: 16,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: '#EFEFEF',
    },
    transferBtn: {
        backgroundColor: Colors.brand,
        borderRadius: 10,
        paddingVertical: 15,
        alignItems: 'center',
    },
    transferBtnDisabled: {
        opacity: 0.45,
    },
    transferBtnText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '600',
    },
});
